import { useEffect, useState } from 'react';
import { DataTable, ErrorState, Field, Spinner, StatusBadge } from '../../components/ui.jsx';
import { api } from '../../api/client.js';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { useToast } from '../../state/ToastContext.jsx';
import { timeAgo } from '../../lib/format.js';

// Each setting is described here so the form explains itself instead of showing
// raw keys from the database.
const FIELDS = [
  {
    key: 'platform_name',
    label: 'Platform name',
    type: 'text',
    hint: 'Shown in the header, the footer and outgoing notifications.'
  },
  { key: 'support_email', label: 'Support email', type: 'email' },
  { key: 'support_phone', label: 'Support phone', type: 'tel' },
  {
    key: 'require_listing_approval',
    label: 'Review new listings before they go live',
    type: 'boolean',
    hint: 'Turn this off only if you trust every landlord on the platform.'
  },
  {
    key: 'require_landlord_kyc',
    label: 'Landlords must be verified before publishing',
    type: 'boolean',
    hint: 'Raises trust, but slows down new landlords. Off by default.'
  },
  {
    key: 'max_media_per_property',
    label: 'Maximum files per listing',
    type: 'number',
    hint: 'Pictures and videos together.'
  },
  {
    key: 'commission_percent',
    label: 'Commission percentage',
    type: 'number',
    hint: 'Recorded for reporting. Nothing is charged automatically in this release.'
  },
  {
    key: 'announcement',
    label: 'Site-wide announcement',
    type: 'textarea',
    hint: 'Appears as a banner under the header for everybody. Leave empty for no banner.'
  },
  {
    key: 'advert_link',
    label: 'Advert link',
    type: 'text',
    hint: 'Where the advert photo or video below sends people when they click it. Leave empty for no link.'
  }
];

const BRANDING_SLOTS = [
  {
    slot: 'hero',
    label: 'Homepage background',
    hint: 'Shown behind "Find your next house in Cameroon" on the homepage. A photo or a short video.',
    urlKey: 'hero_media_url',
    typeKey: 'hero_media_type'
  },
  {
    slot: 'advert',
    label: 'Advert',
    hint: 'A photo or video advert shown to visitors. Pairs with the advert link above.',
    urlKey: 'advert_media_url',
    typeKey: 'advert_media_type'
  }
];

const BrandingUploader = ({ slot, label, hint, urlKey, typeKey, settings, onChanged }) => {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const url = settings?.[urlKey];
  const type = settings?.[typeKey];

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { settings: updated } = await api.upload(`/admin/settings/branding/${slot}`, formData);
      onChanged(updated);
      toast.success(`${label} updated.`);
    } catch (requestError) {
      toast.error(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const { settings: updated } = await api.del(`/admin/settings/branding/${slot}`);
      onChanged(updated);
      toast.success(`${label} removed.`);
    } catch (requestError) {
      toast.error(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="branding-uploader">
      <strong>{label}</strong>
      {hint ? <em>{hint}</em> : null}

      {url ? (
        <div className="branding-preview">
          {type === 'video' ? (
            <video src={url} muted loop autoPlay playsInline />
          ) : (
            <img src={url} alt={`${label} preview`} />
          )}
        </div>
      ) : (
        <p className="muted">No {label.toLowerCase()} set yet.</p>
      )}

      <div className="row-actions">
        <label className="btn btn-ghost">
          {url ? 'Replace' : 'Upload'} photo or video
          <input type="file" accept="image/*,video/*" hidden onChange={upload} disabled={busy} />
        </label>
        {url ? (
          <button type="button" className="btn btn-ghost" onClick={remove} disabled={busy}>
            Remove
          </button>
        ) : null}
      </div>
    </div>
  );
};

const SMS_STATUS = {
  sent: { status: 'available', label: 'Sent' },
  queued: { status: 'pending', label: 'Waiting' },
  failed: { status: 'rejected', label: 'Not sent' }
};

/**
 * Notifications reach phones on WhatsApp (Meta's Cloud API) when that is set
 * up, else as texts from an Android phone running the SMS Gateway app. This
 * shows which is set up, sends a test, and lists what went out lately.
 */
const TextMessagesPanel = () => {
  const toast = useToast();
  const sms = useFetch('/admin/sms');
  const [phone, setPhone] = useState('');
  const [testing, setTesting] = useState(false);

  const test = async (channel) => {
    setTesting(true);
    try {
      const result = await api.post('/admin/sms/test', { phone, channel });
      if (result.sent) {
        toast.success(
          channel === 'whatsapp'
            ? `WhatsApp took the test message for ${result.phone}. It should arrive in a moment.`
            : `The gateway took the test text for ${result.phone}. Whether the phone sent it shows below in a minute.`
        );
      }
      else toast.error(`Not sent: ${result.detail}`);
      sms.reload();
    } catch (requestError) {
      toast.error(requestError.message);
    } finally {
      setTesting(false);
    }
  };

  if (sms.loading && !sms.data) return <Spinner />;
  if (sms.error) return <ErrorState message={sms.error} onRetry={sms.reload} />;
  const { configured, whatsapp, whatsappTemplate, counts, recent } = sms.data;

  return (
    <section className="panel">
      <h2>WhatsApp and text messages</h2>
      {whatsapp ? (
        <p className="muted">
          <strong>WhatsApp is on.</strong> Every notification in the bell also goes to the person on WhatsApp, as the
          approved template "{whatsappTemplate}".{' '}
          {configured ? 'If WhatsApp cannot deliver one, it is sent as a text instead.' : null}
        </p>
      ) : (
        <div className="notice notice-info">
          <strong>WhatsApp is not set up yet.</strong> Once Meta's WhatsApp Cloud API is connected (WHATSAPP_TOKEN and
          WHATSAPP_PHONE_NUMBER_ID in the server settings), notifications go on WhatsApp instead of by text.
        </div>
      )}
      {configured ? (
        <p className="muted">
          <strong>Texting is on</strong>, from the Android phone running the SMS Gateway app
          {whatsapp ? ', for anybody WhatsApp cannot reach' : ': every notification in the bell is also texted'}.
        </p>
      ) : (
        <div className="notice notice-warn">
          <strong>Texting is not set up yet.</strong> Install the SMS Gateway app on an Android phone with a SIM, turn on
          its Cloud server, and put the username and password it shows into the server settings as SMS_GATEWAY_USER and
          SMS_GATEWAY_PASS. Email and the bell work either way.
        </div>
      )}

      <p className="muted">
        {counts.sent} sent, {counts.queued} waiting, {counts.failed} not sent.
      </p>

      {configured || whatsapp ? (
        <form
          className="row-actions"
          onSubmit={(event) => {
            event.preventDefault();
            test(whatsapp ? 'whatsapp' : 'sms');
          }}
        >
          <input
            type="tel"
            placeholder="6XX XX XX XX"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            aria-label="Phone number for a test text"
            required
          />
          {whatsapp ? (
            <button type="submit" className="btn btn-primary" disabled={testing}>
              {testing ? 'Sending...' : 'Send a WhatsApp test'}
            </button>
          ) : null}
          {configured ? (
            <button
              type={whatsapp ? 'button' : 'submit'}
              className={`btn ${whatsapp ? 'btn-outline' : 'btn-primary'}`}
              disabled={testing || (whatsapp && !phone.trim())}
              onClick={whatsapp ? () => test('sms') : undefined}
            >
              {testing ? 'Sending...' : 'Send a test text'}
            </button>
          ) : null}
        </form>
      ) : null}

      {recent.length ? (
        <DataTable
          columns={[
            { key: 'to', header: 'To', render: (row) => (row.to ? `${row.to} (${row.phone})` : row.phone) },
            { key: 'channel', header: 'By', render: (row) => (row.channel === 'whatsapp' ? 'WhatsApp' : 'SMS') },
            { key: 'text', header: 'Text', render: (row) => <span className="muted">{row.text}</span> },
            {
              key: 'status',
              header: 'Status',
              render: (row) => (
                <div>
                  <StatusBadge status={SMS_STATUS[row.status].status} label={SMS_STATUS[row.status].label} />
                  {row.status !== 'sent' && row.detail ? <span className="muted"> {row.detail}</span> : null}
                </div>
              )
            },
            { key: 'when', header: 'When', render: (row) => timeAgo(row.sentAt || row.createdAt) }
          ]}
          rows={recent}
          rowKey={(row) => row.id}
          empty={{ title: 'No texts yet' }}
        />
      ) : null}
    </section>
  );
};

const AdminSettings = () => {
  useTitle('Platform settings');
  const toast = useToast();
  const { data, loading, error, reload } = useFetch('/admin/settings');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) setForm(data.settings);
  }, [data]);

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = Object.fromEntries(FIELDS.map((field) => [field.key, String(form[field.key] ?? '')]));
      await api.put('/admin/settings', payload);
      toast.success('Settings saved. Reload the page to see the header and footer update.');
      reload();
    } catch (requestError) {
      toast.error(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) return <Spinner />;
  if (error) {
    return (
      <div className="container section">
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  }

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>Platform settings</h1>
          <p className="muted">These take effect immediately for everybody.</p>
        </div>
      </header>

      <form className="panel settings-form" onSubmit={submit}>
        {FIELDS.map((field) => {
          if (field.type === 'boolean') {
            return (
              <label key={field.key} className="check check-block">
                <input
                  type="checkbox"
                  checked={form[field.key] === 'true'}
                  onChange={(event) => set(field.key, event.target.checked ? 'true' : 'false')}
                />
                <span>
                  <strong>{field.label}</strong>
                  {field.hint ? <em>{field.hint}</em> : null}
                </span>
              </label>
            );
          }

          return (
            <Field key={field.key} label={field.label} hint={field.hint}>
              {field.type === 'textarea' ? (
                <textarea rows="3" value={form[field.key] ?? ''} onChange={(event) => set(field.key, event.target.value)} />
              ) : (
                <input
                  type={field.type}
                  value={form[field.key] ?? ''}
                  onChange={(event) => set(field.key, event.target.value)}
                />
              )}
            </Field>
          );
        })}

        <div className="row-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save settings'}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setForm(data.defaults)}
            disabled={saving}
          >
            Reset to defaults
          </button>
        </div>
      </form>

      <section className="panel branding-panel">
        <h2>Homepage branding</h2>
        <p className="muted">These upload straight away — no need to press Save settings.</p>
        {BRANDING_SLOTS.map((slot) => (
          <BrandingUploader key={slot.slot} {...slot} settings={form} onChanged={setForm} />
        ))}
      </section>

      <TextMessagesPanel />

      <section className="panel">
        <h2>What is not in this release</h2>
        <ul className="plain-list">
          <li>
            <strong>Online rent payment.</strong> Rent and deposits are settled directly between tenant and landlord.
            Mobile money integration is the obvious next step.
          </li>
          <li>
            <strong>Automatic WhatsApp.</strong> WhatsApp charges for messages a business starts, so notices are sent on
            WhatsApp by hand, one tap each, from the notices page and the tenant list.
          </li>
          <li>
            <strong>Automatic tenant screening.</strong> Landlords read the documents themselves.
          </li>
        </ul>
      </section>
    </div>
  );
};

export default AdminSettings;
